import { createStore } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import CloneUtils from '../../../../../../../utils/cloneUtils';
import ScaleUtil from '../../../../../../../utils/scaleUtil';
import {
    DescriptorsMapType,
    ImportDescriptorIdType,
    ReportIdType,
    ReportsMapType,
    DescriptorsToLabelsMapType,
    DescriptorsToScalesMapType,
    DescriptorsToTypesMapType,
    ImportDescriptorDataTypeType,
} from '../../../../types';
import { validateValue } from './utils';
import Assert from '../../../../../../../utils/asserts';
import { FIELD_VALUE_SOURCE } from '../../../../constants/generalConstants';

type NavigationStateType = {
    activeCoordinates: {
        activeRow: number | null;
        activeCol: number | null;
        maxRow: number | null;
        maxCol: number | null;
    };
};

type DescriptorsStateType = {
    descriptorsMap: DescriptorsMapType;
    initialDescriptorsToLabels: DescriptorsToLabelsMapType;
    initialDescriptorsToTypes: DescriptorsToTypesMapType;
    initialDescriptorsToScales: DescriptorsToScalesMapType;
    // TODO: These 3 fields should be combined into one field indicating if descriptors are being edited(edit Mode)
    labelEditing: boolean;
    typeEditing: boolean;
    scaleEditing: boolean;
};

type ReportsStateType = {
    reportsMap: ReportsMapType;
    initialReportsMap: ReportsMapType;
};

type ReportsErrorStateType = {
    reportsErrors: {
        [reportId: ReportIdType]: {
            [descriptorId: ImportDescriptorIdType]: { error: boolean; message: string };
        };
    };
};

type DescriptorsActionsType = {
    descriptorActions: {
        descriptorsEditMode: () => boolean;

        enableLabelEditMode: () => void;
        disableLabelEditMode: () => void;

        enableTypeEditMode: () => void;
        disableTypeEditMode: () => void;

        enableScaleEditMode: () => void;
        disableScaleEditMode: () => void;

        updateDescriptorLabel: (descriptorId: ImportDescriptorIdType, label: string) => void;
        updateDescriptorType: (descriptorId: ImportDescriptorIdType, type: ImportDescriptorDataTypeType) => void;
        updateDescriptorScale: (descriptorId: ImportDescriptorIdType, scale: number) => void;

        resetDescriptorsLabels: () => void;
        resetDescriptorsTypes: () => void;
        resetDescriptorsScales: () => void;

        cancelDescriptorLabelEditing: () => void;
        cancelDescriptorTypeEditing: () => void;
        cancelDescriptorScaleEditing: () => void;

        enableDescriptorsEditMode: () => void;
        cancelDescriptorsEditing: () => void;
        disableDescriptorsEditMode: () => void;

        setDescriptorsLabels: (descriptorsToLabels: DescriptorsToLabelsMapType) => void;
    };
};

type ReportsActionsType = {
    reportsActions: {
        reportsEditMode: () => boolean;

        enableReportEditMode: (reportId: ReportIdType) => void;
        disableReportEditMode: (reportId: ReportIdType) => void;

        updateReportField: (descriptorId: ImportDescriptorIdType, reportId: ReportIdType, value: any) => void;

        resetReportState: (reportId: ReportIdType) => void;
        cancelReportEditing: (reportId: ReportIdType) => void;

        enableAllReportsEditMode: () => void;
        cancelAllReportsEditing: () => void;
        disableAllReportsEditMode: () => void;

        enableReportsEditing: () => void;
        inverseReportsFieldValues: (descriptorId: ImportDescriptorIdType) => void;
        updateReportFieldCalculationDisabled: (
            descriptorId: ImportDescriptorIdType,
            reportId: ReportIdType,
            value: boolean
        ) => void;
    };
};

type ReportsErrorActionsType = {
    reportsErrorActions: {
        updateErrors: (
            descriptorId: ImportDescriptorIdType,
            reportId: ReportIdType,
            error: { error: boolean; message: string }
        ) => void;
    };
};

type NavigationActionsType = {
    coordinatesActions: {
        setCoordinates: ({ activeRow, activeCol }: { activeRow: number | null; activeCol: number | null }) => void;
    };
};

export type ImportTableStateType = DescriptorsStateType &
    ReportsStateType &
    ReportsErrorStateType &
    NavigationStateType;
export type ImportTableActionsType = DescriptorsActionsType &
    ReportsActionsType &
    ReportsErrorActionsType &
    NavigationActionsType;

export type ImportTableStoreType = ImportTableStateType & ImportTableActionsType;

const createImportTableStore = (initialState: ImportTableStateType) => {
    const DEFAULT_STATE: ImportTableStateType = {
        activeCoordinates: { activeCol: null, activeRow: null, maxRow: null, maxCol: null },
        descriptorsMap: {},
        reportsMap: {},
        reportsErrors: {},
        initialReportsMap: {},
        initialDescriptorsToLabels: {},
        initialDescriptorsToScales: {},
        initialDescriptorsToTypes: {},
        labelEditing: false,
        typeEditing: false,
        scaleEditing: false,
    } as const;

    return createStore<ImportTableStoreType>()(
        devtools(
            immer((set, get) => ({
                ...DEFAULT_STATE,
                ...initialState,
                descriptorActions: {
                    descriptorsEditMode: () => {
                        return get().labelEditing && get().typeEditing && get().scaleEditing;
                    },
                    enableLabelEditMode: () =>
                        set((state) => {
                            state.labelEditing = true;
                        }),
                    disableLabelEditMode: () =>
                        set((state) => {
                            state.labelEditing = false;
                        }),
                    enableTypeEditMode: () =>
                        set((state) => {
                            state.typeEditing = true;
                        }),
                    disableTypeEditMode: () =>
                        set((state) => {
                            state.typeEditing = false;
                        }),
                    enableScaleEditMode: () =>
                        set((state) => {
                            state.scaleEditing = true;
                        }),
                    disableScaleEditMode: () =>
                        set((state) => {
                            state.scaleEditing = false;
                        }),
                    updateDescriptorLabel: (descriptorId, label) =>
                        set((state) => {
                            Assert.trueVal(state.descriptorsMap.hasOwnProperty(descriptorId));
                            if (state.descriptorsMap.hasOwnProperty(descriptorId)) {
                                state.descriptorsMap[descriptorId].label = label;
                            }
                        }),
                    updateDescriptorType: (descriptorId, type) =>
                        set((state) => {
                            Assert.trueVal(state.descriptorsMap.hasOwnProperty(descriptorId));
                            if (state.descriptorsMap.hasOwnProperty(descriptorId)) {
                                state.descriptorsMap[descriptorId].dataType = type;
                            }
                        }),
                    updateDescriptorScale: (descriptorId, scale) =>
                        set((state) => {
                            Assert.trueVal(state.descriptorsMap.hasOwnProperty(descriptorId));
                            if (state.descriptorsMap.hasOwnProperty(descriptorId)) {
                                state.descriptorsMap[descriptorId].inputScale.value = scale;
                                state.descriptorsMap[descriptorId].inputScale.name = ScaleUtil.numToTextRepresentation(
                                    scale,
                                    true
                                ).toUpperCase();
                            }
                        }),
                    resetDescriptorsLabels: () => {
                        for (const [key, value] of Object.entries(get().initialDescriptorsToLabels)) {
                            get().descriptorActions.updateDescriptorLabel(key, value);
                        }
                    },
                    resetDescriptorsTypes: () => {
                        for (const [key, value] of Object.entries(get().initialDescriptorsToTypes)) {
                            get().descriptorActions.updateDescriptorType(key, value);
                        }
                    },
                    resetDescriptorsScales: () => {
                        for (const [key, value] of Object.entries(get().initialDescriptorsToScales)) {
                            get().descriptorActions.updateDescriptorScale(key, value);
                        }
                    },
                    cancelDescriptorLabelEditing: () => {
                        get().descriptorActions.disableLabelEditMode();
                        get().descriptorActions.resetDescriptorsLabels();
                    },
                    cancelDescriptorTypeEditing: () => {
                        get().descriptorActions.disableTypeEditMode();
                        get().descriptorActions.resetDescriptorsTypes();
                    },
                    cancelDescriptorScaleEditing: () => {
                        get().descriptorActions.disableScaleEditMode();
                        get().descriptorActions.resetDescriptorsScales();
                    },

                    enableDescriptorsEditMode: () => {
                        if (!get().descriptorActions.descriptorsEditMode() && !get().reportsActions.reportsEditMode()) {
                            get().descriptorActions.enableLabelEditMode();
                            get().descriptorActions.enableTypeEditMode();
                            get().descriptorActions.enableScaleEditMode();
                        }
                    },
                    cancelDescriptorsEditing: () => {
                        get().descriptorActions.cancelDescriptorLabelEditing();
                        get().descriptorActions.cancelDescriptorTypeEditing();
                        get().descriptorActions.cancelDescriptorScaleEditing();
                    },
                    disableDescriptorsEditMode: () => {
                        get().descriptorActions.disableLabelEditMode();
                        get().descriptorActions.disableTypeEditMode();
                        get().descriptorActions.disableScaleEditMode();
                    },
                    setDescriptorsLabels: (descriptorsToLabels) =>
                        set((state) => {
                            for (const descriptorId of Object.keys(descriptorsToLabels)) {
                                state.descriptorsMap[descriptorId].label = descriptorsToLabels[descriptorId];
                            }
                        }),
                },
                reportsActions: {
                    reportsEditMode: () => {
                        return Object.values(get().reportsMap).every((report) => report.editing);
                    },
                    enableReportEditMode: (reportId) =>
                        set((state) => {
                            if (state.reportsMap[reportId]) {
                                state.reportsMap[reportId].editing = true;
                            }
                        }),
                    disableReportEditMode: (reportId) =>
                        set((state) => {
                            if (state.reportsMap[reportId]) {
                                state.reportsMap[reportId].editing = false;
                            }
                        }),
                    updateReportField: (descriptorId, reportId, value) => {
                        set((state) => {
                            Assert.trueVal(!!state.reportsMap[reportId]);
                            if (state.reportsMap[reportId]) {
                                if (state.reportsMap[reportId].rowIdToValue[descriptorId]) {
                                    state.reportsMap[reportId].rowIdToValue[descriptorId].normalizedValue = value;
                                }
                            }
                        });

                        const error = validateValue(get().descriptorsMap[descriptorId], value);
                        get().reportsErrorActions.updateErrors(descriptorId, reportId, error);
                    },
                    resetReportState: (reportId) =>
                        set((state) => {
                            const initialReportsMap = get().initialReportsMap;
                            Assert.trueVal(!!initialReportsMap[reportId]);
                            if (initialReportsMap[reportId]) {
                                const initialReport = CloneUtils.deepClone(initialReportsMap[reportId]);
                                state.reportsMap[reportId] = initialReport;
                            }
                        }),
                    cancelReportEditing: (reportId) => {
                        get().reportsActions.disableReportEditMode(reportId);
                        get().reportsActions.resetReportState(reportId);
                    },
                    enableAllReportsEditMode: () => {
                        for (const reportId of Object.keys(get().reportsMap)) {
                            get().reportsActions.enableReportEditMode(reportId);
                        }
                    },
                    cancelAllReportsEditing: () => {
                        for (const reportId of Object.keys(get().reportsMap)) {
                            get().reportsActions.cancelReportEditing(reportId);
                        }
                    },
                    disableAllReportsEditMode: () =>
                        set((state) => {
                            for (const reportId of Object.keys(state.reportsMap)) {
                                state.reportsMap[reportId].editing = false;
                            }
                        }),
                    enableReportsEditing: () => {
                        if (!get().descriptorActions.descriptorsEditMode() && !get().reportsActions.reportsEditMode()) {
                            get().reportsActions.enableAllReportsEditMode();
                        }
                    },
                    inverseReportsFieldValues: (descriptorId) => {
                        for (const reportId of Object.keys(get().reportsMap)) {
                            const fieldValue = get().reportsMap[reportId].rowIdToValue[descriptorId];
                            if (
                                typeof fieldValue.normalizedValue === 'number' &&
                                fieldValue.source !== FIELD_VALUE_SOURCE.MASTER_TABLE
                            ) {
                                const newValue = fieldValue.normalizedValue * -1;
                                get().reportsActions.updateReportField(descriptorId, reportId, newValue);
                            }
                        }
                    },
                    updateReportFieldCalculationDisabled: (descriptorId, reportId, value) =>
                        set((state) => {
                            Assert.trueVal(!!state.reportsMap[reportId]);
                            Assert.trueVal(!!state.reportsMap[reportId].rowIdToValue[descriptorId]);
                            if (state.reportsMap[reportId]) {
                                if (state.reportsMap[reportId].rowIdToValue[descriptorId]) {
                                    state.reportsMap[reportId].rowIdToValue[descriptorId].manuallyDisabledVerticalCalc =
                                        value;
                                }
                            }
                        }),
                },
                reportsErrorActions: {
                    updateErrors: (descriptorId, reportId, error) =>
                        set((state) => {
                            if (!state.reportsErrors[reportId]) {
                                state.reportsErrors[reportId] = {};
                            }

                            if (!state.reportsErrors[reportId][descriptorId]) {
                                state.reportsErrors[reportId][descriptorId] = { error: false, message: '' };
                            }

                            if (error.error) {
                                state.reportsErrors[reportId][descriptorId].error = true;
                                state.reportsErrors[reportId][descriptorId].message = error.message;
                            } else {
                                state.reportsErrors[reportId][descriptorId].error = false;
                                state.reportsErrors[reportId][descriptorId].message = '';
                            }
                        }),
                },
                coordinatesActions: {
                    setCoordinates: ({ activeRow, activeCol }) =>
                        set((state) => {
                            if (activeRow !== null && state.activeCoordinates.maxRow) {
                                state.activeCoordinates.activeRow = Math.max(
                                    0,
                                    Math.min(activeRow, state.activeCoordinates.maxRow)
                                );
                            } else {
                                state.activeCoordinates.activeRow = activeRow;
                            }

                            if (activeCol !== null && state.activeCoordinates.maxCol) {
                                state.activeCoordinates.activeCol = Math.max(
                                    0,
                                    Math.min(activeCol, state.activeCoordinates.maxCol)
                                );
                            } else {
                                state.activeCoordinates.activeCol = activeCol;
                            }
                        }),
                },
            }))
        )
    );
};

export default createImportTableStore;
